import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Store, ShoppingBag, CheckCircle2, Package, ArrowUpRight, Tag, Zap, LayoutTemplate } from "lucide-react";
import { toast } from "sonner";

const fmtMoney = (n, ccy = "GBP") => {
  if (!n || n <= 0) return "Free";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: ccy }).format(n);
  } catch {
    return `${ccy} ${n.toFixed(2)}`;
  }
};

const METHODOLOGY_BADGE = {
  agile: "bg-emerald-50 text-emerald-800 border-emerald-200",
  waterfall: "bg-blue-50 text-blue-800 border-blue-200",
  hybrid: "bg-purple-50 text-purple-800 border-purple-200",
};

// Each add-on type declares how it's browsed, its "buy" verb, and where to
// send an owner to use it. Add a row here (and the matching backend listing
// fn in routes/marketplace.py) to sell a new kind of add-on.
const ITEM_TYPES = [
  { key: "template", label: "Templates", icon: LayoutTemplate, useLink: "/templates", useLabel: "Use it", myLink: "/templates" },
  { key: "automation_pack", label: "Automation packs", icon: Zap, useLink: "/automations", useLabel: "Install", myLink: "/automations" },
];

const Marketplace = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);
  const [tab, setTab] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/marketplace/listings");
      setItems(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load marketplace");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(
    () => (tab === "all" ? items : items.filter((i) => i.item_type === tab)),
    [items, tab]
  );

  const buy = async (item) => {
    const key = `${item.item_type}:${item.item_id}`;
    setBuying(key);
    try {
      const { data } = await api.post(`/marketplace/listings/${item.item_type}/${item.item_id}/purchase`);
      if (data.status === "fulfilled") {
        toast.success("Added to your workspace");
        load();
      } else if (data.status === "already_owned") {
        toast.info("You already own this");
        load();
      } else if (data.status === "checkout_required" && data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start purchase");
    } finally {
      setBuying(null);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Marketplace"
        title="Add-on Marketplace"
        description="Browse and purchase templates, automation packs and other add-ons published by other PMO teams. Buying one adds it straight to your workspace — yours forever."
        actions={
          <div className="flex gap-2">
            <Link to="/templates">
              <Button variant="outline" className="rounded-none border-gray-900" data-testid="back-to-my-templates">
                <Package className="w-4 h-4 mr-1" /> My templates
              </Button>
            </Link>
            <Link to="/automations">
              <Button variant="outline" className="rounded-none border-gray-900" data-testid="back-to-my-automations">
                <Zap className="w-4 h-4 mr-1" /> My automations
              </Button>
            </Link>
          </div>
        }
      />

      <div className="px-5 sm:px-8 lg:px-12 pt-6 flex items-center gap-2 border-b border-[#1F2937]">
        {[{ key: "all", label: "All add-ons" }, ...ITEM_TYPES].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`text-xs uppercase tracking-[0.2em] px-3 py-2 border-b-2 -mb-px transition-colors ${
              tab === t.key ? "border-[#002FA7] text-white" : "border-transparent text-gray-500 hover:text-gray-300"
            }`}
            data-testid={`marketplace-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="px-5 sm:px-8 lg:px-12 py-8">
        {loading ? (
          <div className="text-sm text-gray-500">Loading marketplace…</div>
        ) : visible.length === 0 ? (
          <div className="border border-dashed border-[#374151] p-16 text-center">
            <Store className="w-10 h-10 mx-auto text-gray-300" strokeWidth={1.3} />
            <div className="font-display font-black text-2xl tracking-tighter mt-4">No add-ons yet</div>
            <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
              Be the first to publish one — share your playbook or automation pack and earn from it.
            </p>
            <div className="flex items-center justify-center gap-3 mt-5">
              <Link to="/templates">
                <Button className="rounded-none bg-[#002FA7] hover:bg-[#002480]">Go to my templates</Button>
              </Link>
              <Link to="/automations">
                <Button variant="outline" className="rounded-none border-gray-900">Go to my automations</Button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1F2937]">
            {visible.map((item) => {
              const typeMeta = ITEM_TYPES.find((t) => t.key === item.item_type);
              const TypeIcon = typeMeta?.icon || Package;
              const key = `${item.item_type}:${item.item_id}`;
              return (
                <article
                  key={key}
                  className="bg-[#0D1117] p-6 flex flex-col"
                  data-testid={`marketplace-card-${item.item_id}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-gray-500">
                      <TypeIcon className="w-3 h-3" strokeWidth={1.7} /> {typeMeta?.label || item.item_type}
                    </span>
                    {item.item_type === "template" && item.detail?.methodology && (
                      <span className={`text-[10px] uppercase tracking-[0.2em] px-1.5 py-0.5 border ${METHODOLOGY_BADGE[item.detail.methodology] || METHODOLOGY_BADGE.hybrid}`}>
                        {item.detail.methodology}
                      </span>
                    )}
                    {item.already_purchased && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-emerald-700">
                        <CheckCircle2 className="w-3 h-3" strokeWidth={2} /> Owned
                      </span>
                    )}
                  </div>
                  <h3 className="font-display font-black text-2xl tracking-tighter leading-tight" data-testid={`marketplace-name-${item.item_id}`}>
                    {item.name}
                  </h3>
                  <p className="text-sm text-slate-400 mt-2 line-clamp-3 flex-1">
                    {item.description || "No description provided."}
                  </p>

                  <div className="mt-4 space-y-1.5 text-xs text-gray-500">
                    <div className="flex items-center gap-2">
                      <Package className="w-3 h-3" strokeWidth={1.7} />
                      <span>
                        <strong className="font-mono text-slate-300">
                          {item.item_type === "automation_pack" ? item.detail?.rule_count : item.detail?.item_count}
                        </strong>{" "}
                        {item.item_type === "automation_pack" ? "automation rule(s)" : "tasks / phases"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Store className="w-3 h-3" strokeWidth={1.7} />
                      <span>by {item.publisher_name || "Anonymous"}</span>
                    </div>
                  </div>

                  <div className="mt-5 pt-4 border-t border-[#1F2937] flex items-end justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 flex items-center gap-1">
                        <Tag className="w-2.5 h-2.5" strokeWidth={1.7} /> Price
                      </div>
                      <div className="font-display font-black text-2xl tracking-tighter font-mono mt-0.5" data-testid={`marketplace-price-${item.item_id}`}>
                        {fmtMoney(item.price, item.currency)}
                      </div>
                    </div>
                    {item.already_purchased ? (
                      <Link to={typeMeta?.useLink || "/templates"}>
                        <Button variant="outline" className="rounded-none border-gray-900" data-testid={`marketplace-open-${item.item_id}`}>
                          {typeMeta?.useLabel || "Use it"} <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
                        </Button>
                      </Link>
                    ) : (
                      <Button
                        onClick={() => buy(item)}
                        disabled={buying === key}
                        className="rounded-none bg-[#002FA7] hover:bg-[#002480]"
                        data-testid={`marketplace-buy-${item.item_id}`}
                      >
                        <ShoppingBag className="w-4 h-4 mr-1" />
                        {buying === key ? "…" : (item.price > 0 ? "Buy" : "Get free")}
                      </Button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Marketplace;
